import * as React from "react";
import { Link, useLocation } from "react-router";
import { ArrowLeft } from "lucide-react";

import { Button } from "./button";
import { Portal, PortalHost } from "./portal";
import { Separator } from "./separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "./sidebar";
import { TooltipProvider } from "./tooltip";

const HEADER_ACTIONS_ID = "app-shell-header-actions";

/** Renders a page's primary actions at the right of the shell header. */
export function HeaderActions({ children }: { children: React.ReactNode }) {
  return <Portal id={HEADER_ACTIONS_ID}>{children}</Portal>;
}

// Keyed on `location.key` rather than the pathname so tapping the link of the
// current page also dismisses the sheet.
function CloseMobileSidebarOnNavigation() {
  const { key } = useLocation();
  const { setOpenMobile } = useSidebar();
  React.useEffect(() => {
    setOpenMobile(false);
  }, [key, setOpenMobile]);
  return null;
}

interface AppShellProps {
  sidebar: React.ReactNode;
  title?: React.ReactNode;
  backTo?: string;
  children: React.ReactNode;
}

export function AppShell({ sidebar, title, backTo, children }: AppShellProps) {
  return (
    <TooltipProvider>
      <SidebarProvider className="h-svh">
        <CloseMobileSidebarOnNavigation />
        {sidebar}
        <SidebarInset className="min-w-0 overflow-hidden">
          <header className="flex h-header shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mx-1 data-vertical:h-4 data-vertical:self-center"
            />
            {backTo && (
              <Button variant="ghost" size="icon-sm" asChild>
                <Link
                  to={backTo}
                  viewTransition
                  relative="path"
                  aria-label="Volver"
                >
                  <ArrowLeft />
                </Link>
              </Button>
            )}
            <h1 className="truncate text-base font-medium">{title}</h1>
            <PortalHost
              id={HEADER_ACTIONS_ID}
              className="ml-auto flex items-center gap-2"
            />
          </header>
          <div className="flex-1 overflow-y-auto p-4 md:p-6">{children}</div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
